import { useMemo } from "react";

import { useAppSelector } from "@/store";

/**
 * `settings.web_design_type` — `ONLINE_ORDERING_DESIGN_TYPE_ENUM`.
 *
 * **SIX members, and there is no bare `SLATE` and no bare `VIBRANT`.** The
 * schema default is `CLASSIC`, not `MODERN`; anything missing or unknown
 * leaves every flag false, which renders exactly like `CLASSIC`.
 *
 * The original reads this **from Redux in every consumer** — never from
 * context, never from the theme — and derives the same five booleans with the
 * same `startsWith` / `endsWith` / `includes` tests each time. The design type
 * does NOT touch the palette: only `brand_color` and `dark_mode` do.
 */
export const ONLINE_ORDERING_DESIGN_TYPE_ENUM = {
  CLASSIC: "CLASSIC",
  CLASSIC_VIBRANT: "CLASSIC_VIBRANT",
  MODERN: "MODERN",
  MODERN_SLATE: "MODERN_SLATE",
  MODERN_VIBRANT: "MODERN_VIBRANT",
  MODERN_SLATE_VIBRANT: "MODERN_SLATE_VIBRANT",
} as const;

export type OnlineOrderingDesignType =
  (typeof ONLINE_ORDERING_DESIGN_TYPE_ENUM)[keyof typeof ONLINE_ORDERING_DESIGN_TYPE_ENUM];

export interface DesignFlags {
  /** The raw setting, so a consumer can compare against the enum directly. */
  designType: string;
  /** `A` — `startsWith("MODERN")`. */
  isModernDesign: boolean;
  /** `N` — `endsWith("VIBRANT")`. */
  isVibrantDesign: boolean;
  /** `F` — `includes("SLATE")`. Selects the Slate navbar. */
  isSlateDesign: boolean;
  /**
   * `Y` — `endsWith("SLATE")`.
   *
   * ⚠ This is **false for `MODERN_SLATE_VIBRANT`**, so that design gets the
   * SLATE navbar (`includes`) with the VIBRANT tab items (`endsWith`). It is a
   * quirk of the two different tests, it is the shipped behaviour, and it is
   * reproduced deliberately — do not "fix" it to `includes("SLATE")`.
   */
  isModernSlateDesign: boolean;
  /** `B` — strict equality with `MODERN_VIBRANT`. */
  isModernVibrantDesign: boolean;
}

/** The pure half, so it can be unit-tested without a store. */
export const designFlags = (webDesignType?: string | null): DesignFlags => {
  const designType = String(webDesignType ?? "");
  return {
    designType,
    isModernDesign: designType.startsWith("MODERN"),
    isVibrantDesign: designType.endsWith("VIBRANT"),
    isSlateDesign: designType.includes("SLATE"),
    isModernSlateDesign: designType.endsWith("SLATE"),
    isModernVibrantDesign:
      designType === ONLINE_ORDERING_DESIGN_TYPE_ENUM.MODERN_VIBRANT,
  };
};

/** The Redux half — one `useSelector` on the setting, memoised on its value. */
export default function useDesignType(): DesignFlags {
  const webDesignType = useAppSelector(
    (state) => state.settings.data.web_design_type,
  );
  return useMemo(() => designFlags(webDesignType), [webDesignType]);
}
